import React, { useState, useContext, useEffect } from "react";
import { AppPage, NoIconAlert } from "../../components";
import { useTranslate, useYearInTitle } from "../../helpers";
import { Button, Choice } from "@cmsgov/ds-medicare-gov";
import Routes from "../../app/routes";
import { PharmacyType, ActionType, PDE } from "../../@types";
import { AppStore } from "../../app/store";
import { RouteComponentProps, withRouter } from "react-router";
import {
  AnalyticsActionType,
  AnalyticsButtonStyle,
  AnalyticsButtonType,
  Ga4Event,
} from "../../app/contexts/Analytics/types";

interface NextPageProps {
  drugCosts: string;
  pdes: PDE[] | undefined;
  search: string;
}

// Pulled this out into a standalone function in order to make testing easier
export const determineNextPage = ({
  drugCosts,
  pdes,
  search,
}: NextPageProps): string => {
  let uri;

  if (drugCosts === "include") {
    if (pdes && pdes.length) {
      uri = `${Routes.pdePage}${search}`;
    } else {
      uri = `${Routes.managePrescriptions}${search}`;
    }
  } else {
    uri = `${Routes.searchResults}${search}`;
  }

  return uri;
};

const DrugSearchPreferencesPage: React.FunctionComponent<RouteComponentProps & {
  isFromSummaryPage?: boolean;
}> = ({ isFromSummaryPage = false, location, history }) => {
  const [drugCosts, setDrugCosts] = useState("");
  const t = useTranslate();
  const { dispatch, state } = useContext(AppStore.AppContext);
  const showContinueButton = drugCosts === "exclude" || drugCosts === "include";

  useEffect(() => {
    if (!isFromSummaryPage) {
      dispatch({ type: ActionType.RESET_PHARMACIES });
    }
  }, []);

  /* istanbul ignore next: tested elsewhere */
  const goToNextPage = (): void => {
    const uri = determineNextPage({
      drugCosts,
      pdes: state.beneficiary ? state.beneficiary.pdes : [],
      search: location.search,
    });

    dispatch({
      type: AnalyticsActionType.SEND_GA4_EVENT,
      settings: {
        event_name: Ga4Event.DRUG_SEARCH_PREF,
      },
    });
    dispatch({
      type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
      settings: {
        button: {
          buttonStyle: AnalyticsButtonStyle.PRIMARY,
          buttonType: AnalyticsButtonType.BUTTON,
          text: t("continue"),
        },
        linkUrl: uri.toString(),
      },
    });

    history.push(uri);
  };

  // heading is the shown on the page, i.e. <h1>.
  const heading = t("plan_search_prefs.header");
  // title is the page title.
  const title = useYearInTitle(heading, state.year);

  return (
    <AppPage heading={heading} title={title}>
      <div className="DrugSearchPreferencePage ds-u-margin-bottom--3">
        <span className="ds-u-visibility--screen-reader">
          {t("sr_only_additional_questions_may_appear")}
        </span>
        <fieldset className="ds-c-fieldset ds-u-margin--0">
          <legend className="ds-u-margin--0 ds-text-heading--xl ds-u-margin-bottom--1 ds-u-padding--0">
            {t("plan_search_prefs.include_drug_costs")}
          </legend>

          <Choice
            type="radio"
            name="drugCosts"
            value="include"
            className="e2e-drug-search-pref--yes"
            checked={drugCosts === "include"}
            onChange={(): void => {
              dispatch({
                type: AnalyticsActionType.SEND_TEALIUM_EVENT,
                settings: {
                  event_action: "Find Plans - See Drug Costs",
                  event_label: "Yes",
                },
              });
              setDrugCosts("include");
              dispatch({
                type: AnalyticsActionType.SEND_GA4_EVENT,
                settings: {
                  event_name: Ga4Event.RADIO_BUTTON_SELECTED,
                  heading: t("plan_search_prefs.include_drug_costs"),
                  text: t("yes"),
                },
              });
            }}
            checkedChildren={NoIconAlert(
              t("plan_search_prefs.great"),
              t("plan_search_prefs.to_see_drug_costs")
            )}
          >
            {t("yes")}
          </Choice>

          <Choice
            type="radio"
            name="drugCosts"
            value="exclude"
            className="e2e-drug-search-pref--no"
            checked={drugCosts === "exclude"}
            onChange={(): void => {
              dispatch({
                type: AnalyticsActionType.SEND_TEALIUM_EVENT,
                settings: {
                  event_action: "Find Plans - See Drug Costs",
                  event_label: "No",
                },
              });
              dispatch({
                type: ActionType.UPDATE_PHARMACY_TYPE,
                payload: PharmacyType.NONE,
              });
              setDrugCosts("exclude");
              dispatch({
                type: AnalyticsActionType.SEND_GA4_EVENT,
                settings: {
                  event_name: Ga4Event.RADIO_BUTTON_SELECTED,
                  heading: t("plan_search_prefs.include_drug_costs"),
                  text: t("no"),
                },
              });
            }}
            checkedChildren={NoIconAlert(
              t("plan_search_prefs.are_you_sure"),
              t("plan_search_prefs.need_to_enter_drugs")
            )}
          >
            {t("no")}
          </Choice>
        </fieldset>

        {showContinueButton && (
          <section className="ContinueSection">
            <Button
              className="e2e-drug-search-pref--continue mct-u-width--full mct-u-md-width--auto"
              variation="solid"
              onClick={goToNextPage}
            >
              {t("continue")}
            </Button>
          </section>
        )}
      </div>
    </AppPage>
  );
};

export default withRouter(DrugSearchPreferencesPage);
